/**
 * PATH: packages/email-hub/src/feature/signal-strip/ReaderSignalStrip.tsx
 * FILE_ID: EMAILHUB-FEATURE-SIGNALSTRIP-READER-001
 * PURPOSE: Email-hub host for the reader's Signal Strip — wires the LIVE
 *          chips (CategoryChip, EmailTagChips, SenderIdentityCard /
 *          CompanyChip / AddToCrmChip, job chips, ReaderPlacementsControl,
 *          Request-a-rule) into the WHO / WHAT / SIGNALS / WHERE lanes.
 * WHY:     Transplanted VERBATIM from EmailPreviewPane's AiCopilotCard slot
 *          wiring (2026-07-16 redesign) — that wiring encodes a year of
 *          paid-for operator fixes (pin category, Add-to-CRM gating, peek-
 *          first job chip, multi-placement management, request-a-rule
 *          co-pilot routing). Only the ARRANGEMENT changed: every affordance
 *          now sits in a labelled lane that names its downstream meaning.
 * LANES:   WHO    → CRM identity (SenderIdentityCard / CompanyChip / AddToCrm)
 *          WHAT   → workflow category (CategoryChip)
 *          SIGNALS→ micro-detail tags (EmailTagChips — "Verification code" etc.)
 *          WHERE  → AI pip + visibility + board/job routing + placements
 * DECISIONS: Change #269 (D269-11/D269-12) moved the confirm/reject cluster
 *          into the reader-header decision line. The strip renders NO second
 *          decision cluster; while decisions are pending the host passes
 *          suppressDecisionAffordances to hide competing accept/reject chips.
 * DEPENDENCIES: aiCopilotStore (request-a-rule prefers the conversational
 *          co-pilot when mounted — FEAT-2026-07-09-ASKAI-OPENS-COPILOT).
 * RELATED:
 *   - SignalStrip.tsx: the labelled-lane layout shell this feeds.
 *   - components/EmailPreviewPane.tsx: the consuming reader host.
 */
import { useState } from 'react';
import { Briefcase } from 'lucide-react';

import { CategoryChip } from '../components/CategoryChip';
import CostCategoryChip, { shouldShowCostCategory } from '../components/CostCategoryChip';
import { EmailAllocationRail, type AllocationMember } from '../components/EmailAllocationRail';
import { EmailTagChips } from '../components/EmailTagChips';
import SenderIdentityCard, {
  shouldShowSenderIdentity,
  shouldOfferManualCrmFiling,
} from '../components/SenderIdentityCard';
import { AddToCrmChip } from '../components/AddToCrmChip';
import { CompanyChip } from '../components/CompanyChip';
import { SuggestedJobChip } from '../components/SuggestedJobChip';
import { CardAiTag } from '../components/CardAiTag';
import { CardVisibilityBadge } from '../components/CardVisibilityBadge';
import { HomeOrgBadge } from '../components/HomeOrgBadge';
import { RehomeOrgControl } from '../components/RehomeOrgControl';
import { getCategoryConfig } from '../lib/categoryConfig';
import { resolveCardAiState } from '../lib/cardAiState';
import { ReaderPlacementsControl } from '../reader-header';
import { useOrgCustomCategories, usePersonalCategories } from '../hooks/useEmailLabelPrefs';
import { hasAiCopilotHost, useAiCopilotStore } from '../store/aiCopilotStore';
import type { Email } from '../hooks/useEmails';
import { ReferToOrgAction, SuggestedOrgChip } from '../referrals';
import { getSamKanbanApi } from '@quotech/kanban-host';
import {
  InvoiceNumberChip,
  VerificationCodeChip,
  invoiceNumberSignalFor,
  verificationCodeSignalFor,
} from '../action-signals';
import { CompanyContextDrawer } from '../company-context';
import { emailWithSenderIdentity, SenderSignalBadges } from '../sender-identity';

import { SignalStrip } from './SignalStrip';
import { buildLaneMenuActionContext, laneMenuHasActions } from './laneMenuActions';
import {
  ReaderSignalStripMenuButton,
  ReaderSignalStripMenus,
  type LaneMenuPosition,
} from './ReaderSignalStripMenus';

export interface ReaderSignalStripProps {
  email: Email;
  api: ReturnType<typeof getSamKanbanApi>;
  orgId: number | null;
  onRefetch?: () => void;
  /** Narrow local list patch — prefer over onRefetch for chip success paths. */
  onPatchEmail?: (emailId: string, patch: Partial<Email>) => void;
  /** Explicitly scope the inbox list to this sender's company. */
  onFilterCompany?: () => void;
  /** Open the allocated card in the peek drawer. */
  onOpenCard?: (cardId: string) => void;
  /** Peek the allocated board (lane list + counts). */
  onOpenBoard?: () => void;
  /** Peek-first job navigation (triage v5). */
  onPeekJob: (jobId: number) => void;
  /** Feature-detected on the api by the host (older bundles omit the chip). */
  canRequestRule: boolean;
  /** Fallback when no conversational co-pilot host is mounted. */
  onOpenRuleDialog: () => void;
  /** Optional host-provided people list enables Owner/Due/Watchers inline. */
  allocationMembers?: AllocationMember[];
  /** Full-reader embedding can intentionally suppress CRM identity. */
  hideSenderIdentity?: boolean;
  /** Full-reader embedding can intentionally suppress job linking. */
  hideJobLink?: boolean;
  /**
   * While the reader decision line owns the ONE confirm/reject pair
   * (change #269 D269-12), the expanded context must not render competing
   * accept/reject affordances: suggested org/job chips and the AI
   * tick/cross on the placement chip are suppressed.
   */
  suppressDecisionAffordances?: boolean;
}

/** The blue peek-first job pill — shared by both allocation branches. */
function JobPeekChip({ email, onPeekJob }: { email: Email; onPeekJob: (jobId: number) => void }) {
  if (!email.job_id) return null;
  return (
    <button
      type="button"
      onClick={() => onPeekJob(email.job_id!)}
      title={`Peek at ${email.job_name || `Job #${email.job_id}`} - "Open job" inside completes the navigation`}
      data-testid="job-peek-chip"
      className="inline-flex items-center gap-1 px-2 py-0.5 text-[10px] bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300 rounded-full hover:underline max-w-[200px]"
    >
      <Briefcase className="h-2.5 w-2.5 shrink-0" />
      <span className="truncate">{email.job_name || `Job #${email.job_id}`}</span>
    </button>
  );
}

/**
 * ReaderSignalStrip(email + host callbacks) → labelled SignalStrip.
 * Every chip and its gating condition is the proven EmailPreviewPane wiring;
 * this component only decides WHICH LANE each affordance belongs to.
 */
export function ReaderSignalStrip({
  email,
  api,
  orgId,
  onRefetch,
  onPatchEmail,
  onFilterCompany,
  onOpenCard,
  onOpenBoard,
  onPeekJob,
  canRequestRule,
  onOpenRuleDialog,
  allocationMembers,
  hideSenderIdentity = false,
  hideJobLink = false,
  suppressDecisionAffordances = false,
}: ReaderSignalStripProps) {
  const personalCategories = usePersonalCategories();
  const orgCustomCategories = useOrgCustomCategories();
  const customCategories = [...personalCategories, ...orgCustomCategories];
  const catCfg = getCategoryConfig(email.category || 'general', customCategories);
  const CatIcon = catCfg.icon;
  // 3-colour AI status + board visibility — same resolver as the inbox
  // cards so reader and list never drift (operator 2026-06-16).
  const cardAi = resolveCardAiState(email);
  const [companyContextOpen, setCompanyContextOpen] = useState(false);
  const [laneMenu, setLaneMenu] = useState<LaneMenuPosition | null>(null);
  const [dismissedSignals, setDismissedSignals] = useState<Set<string>>(() => new Set());
  const verificationCode = verificationCodeSignalFor(email);
  const invoiceNumber = invoiceNumberSignalFor(email);
  const identityEmail = emailWithSenderIdentity(email);

  const askAi = () => {
    if (hasAiCopilotHost()) {
      useAiCopilotStore
        .getState()
        .openFor(
          email.id,
          `Explain the classification and filing for this email${
            email.from_address ? ` from ${email.from_address}` : ''
          }. `
        );
      return;
    }
    onOpenRuleDialog();
  };

  const who = hideSenderIdentity ? null : shouldShowSenderIdentity(identityEmail) ? (
    <SenderIdentityCard
      email={identityEmail}
      onProfileCreated={onRefetch}
      compact
      // SenderIdentityCard historically delegated overflow to the lane's
      // horizontal scroller. The strip no longer has one: real teammate and
      // CRM controls must wrap inside the bounded Company cell.
      className="!block !min-w-0 !whitespace-normal [&>div]:!flex-wrap"
    />
  ) : identityEmail.company_label ? (
    /* Primary click preserves the reading context in the CRM-proxy drawer.
       Filtering remains an explicit menu/drawer action. */
    <CompanyChip
      label={identityEmail.company_label}
      domain={identityEmail.company_domain}
      onOpenContext={() => setCompanyContextOpen(true)}
      onShowAll={onFilterCompany}
    />
  ) : shouldOfferManualCrmFiling(identityEmail) ? (
    /* Operator 2026-06-16: a sender can be several things (customer OR
       supplier), so offer one-tap multi-type filing. Gated so it never
       re-nags no-reply senders. */
    <AddToCrmChip email={identityEmail} onFiled={onRefetch} />
  ) : null;

  const what = (
    <>
      <CategoryChip
        emailId={email.id}
        category={email.category}
        categoryLocked={email.category_locked}
        pillClass={catCfg.pill}
        icon={CatIcon}
        label={catCfg.label}
        size="sm"
        dotColor={catCfg.hex}
        onChanged={() => onRefetch?.()}
      />
      {shouldShowCostCategory(email) && <CostCategoryChip email={email} />}
    </>
  );

  /* Micro-detail signal vocabulary — "Verification code", "MFA", invoice
     numbers. Depth of tagging = model accuracy gain (operator 2026-07-15),
     so the affordance stays visible even when no tags exist yet. */
  const signals = (
    <>
      {verificationCode && !dismissedSignals.has(verificationCode.kind) && (
        <VerificationCodeChip
          emailId={email.id}
          signal={verificationCode}
          onRejected={() =>
            setDismissedSignals((current) => new Set(current).add(verificationCode.kind))
          }
        />
      )}
      {invoiceNumber && !dismissedSignals.has(invoiceNumber.kind) && (
        <InvoiceNumberChip signal={invoiceNumber} />
      )}
      <EmailTagChips emailId={email.id} tags={email.tags} size="xs" />
      <SenderSignalBadges email={email} />
    </>
  );

  const allocateOrgId = email.organisation ?? orgId;

  const patchSuggestion = (patch: Partial<Email>) => {
    if (onPatchEmail) {
      onPatchEmail(email.id, patch);
      return;
    }
    onRefetch?.();
  };

  const where = (
    <>
      <HomeOrgBadge
        organisationId={email.organisation}
        organisationName={email.organisation_name}
        shellOrgId={orgId}
      />
      <RehomeOrgControl emailId={email.id} onRehomed={onRefetch} />
      {!suppressDecisionAffordances && (
        <SuggestedOrgChip
          emailId={email.id}
          suggested={email.suggested_org}
          onChanged={() => patchSuggestion({ suggested_org: null })}
        />
      )}
      <ReferToOrgAction emailId={email.id} onChanged={onRefetch} />
      <CardAiTag state={cardAi.ai} />
      <CardVisibilityBadge visibility={cardAi.visibility} />
      {!hideJobLink && email.job_id ? (
        <JobPeekChip email={email} onPeekJob={onPeekJob} />
      ) : !hideJobLink && email.suggested_job && !suppressDecisionAffordances ? (
        <SuggestedJobChip
          emailId={email.id}
          suggested={email.suggested_job}
          onConfirmed={() =>
            patchSuggestion({
              job_id: email.suggested_job!.job_id,
              job_name: email.suggested_job!.job_label,
              suggested_job: null,
            })
          }
          onDismissed={() => patchSuggestion({ suggested_job: null })}
        />
      ) : null}
      <ReaderPlacementsControl
        email={email}
        api={api}
        orgId={allocateOrgId}
        onPatchEmail={onPatchEmail}
        onChanged={onRefetch}
        onOpenCard={onOpenCard}
        onOpenBoard={() => onOpenBoard?.()}
        suppressReviewControls={suppressDecisionAffordances}
      />
      {/* Request-a-rule lives on the Filing `…` menu only — duplicating it
          as a chip mashed FILED TO under medium density (BUG-2026-07-21 R2). */}
    </>
  );

  const verificationDismissed = verificationCode
    ? dismissedSignals.has(verificationCode.kind)
    : false;
  const laneActionCtx = buildLaneMenuActionContext({
    companyDomain: identityEmail.company_domain,
    canShowCompanyEmails: Boolean(onFilterCompany),
    hasActiveVerification: Boolean(verificationCode && !verificationDismissed),
    aiSummary: email.ai_summary,
    canRequestRule,
    filedCardId: email.allocation_summary?.card_id,
    filedBoardId: email.allocation_summary?.board_id,
  });

  // BUG-2026-07-17-EMAIL-CONTEXTMENU-COVERAGE: right-click belongs to the
  // canonical EmailContextMenu across the reader. Lane-specific actions stay
  // on the visible `…` buttons only — never intercept contextmenu here.
  const openLaneMenuFromButton = (position: LaneMenuPosition) => {
    if (!laneMenuHasActions(position.lane, laneActionCtx)) return;
    setLaneMenu(position);
  };

  return (
    <>
      <SignalStrip
        who={who}
        what={what}
        signals={signals}
        where={where}
        workflow={
          allocationMembers !== undefined ? (
            <EmailAllocationRail
              variant="inline"
              emailId={email.id}
              assignment={email.assignment ?? null}
              watchers={email.watchers ?? []}
              members={allocationMembers}
              onAssignmentChanged={() => onRefetch?.()}
              onWatchersChanged={() => onRefetch?.()}
            />
          ) : null
        }
        laneActions={{
          who: (
            <ReaderSignalStripMenuButton
              lane="who"
              label="Company actions"
              onOpen={openLaneMenuFromButton}
            />
          ),
          what: (
            <ReaderSignalStripMenuButton
              lane="what"
              label="Classification actions"
              onOpen={openLaneMenuFromButton}
            />
          ),
          signals: (
            <ReaderSignalStripMenuButton
              lane="signals"
              label="Intelligence actions"
              onOpen={openLaneMenuFromButton}
            />
          ),
          where: (
            <ReaderSignalStripMenuButton
              lane="where"
              label="Filing actions"
              onOpen={openLaneMenuFromButton}
            />
          ),
        }}
      />
      <ReaderSignalStripMenus
        menu={laneMenu}
        email={identityEmail}
        verificationCode={verificationCode}
        verificationDismissed={verificationDismissed}
        onClose={() => setLaneMenu(null)}
        onOpenCompanyContext={() => setCompanyContextOpen(true)}
        onShowCompanyEmails={onFilterCompany}
        onOpenCard={onOpenCard}
        onOpenBoard={onOpenBoard}
        canRequestRule={canRequestRule}
        onRequestRule={onOpenRuleDialog}
        onAskAi={askAi}
        onDismissVerification={() => {
          if (verificationCode) {
            setDismissedSignals((current) => new Set(current).add(verificationCode.kind));
          }
        }}
      />
      {identityEmail.company_domain && (
        <CompanyContextDrawer
          open={companyContextOpen}
          domain={identityEmail.company_domain}
          label={identityEmail.company_label || identityEmail.company_domain}
          onClose={() => setCompanyContextOpen(false)}
          onShowEmails={onFilterCompany}
          onChanged={onRefetch}
        />
      )}
    </>
  );
}

export default ReaderSignalStrip;
